/**
 * Observability API client — cost dashboard + audit log read.
 *
 * Shape mirrors os-core/api/observability.py. Pagination is offset-based
 * (deliberately simple) — cursor-style can replace it if our datasets
 * get into multi-million-rows territory.
 */

import { api, type RequestOptions } from "./client";


// ---------- /llm-cost --------------------------------------------------------


export interface CostTotals {
  cost_usd:        number;
  call_count:      number;
  total_tokens:    number;
  avg_latency_ms:  number;
  cache_hit_ratio: number;
}

export interface CostPerProvider  { provider:  string; cost_usd: number; call_count: number; }
export interface CostPerAgent     { agent_id:  string; cost_usd: number; call_count: number; }
export interface CostPerTaskType  { task_type: string; cost_usd: number; call_count: number; }
export interface CostDailyPoint   { day: string; cost_usd: number; call_count: number; }


export interface CostReport {
  window_days:   number;
  since:         string;
  until:         string;
  totals:        CostTotals;
  by_provider:   CostPerProvider[];
  by_agent:      CostPerAgent[];
  by_task_type:  CostPerTaskType[];
  daily:         CostDailyPoint[];
}


// ---------- /audit-log -------------------------------------------------------


export interface AuditLogRow {
  id:          string;
  occurred_at: string;
  actor_type:  string;
  actor_id:    string;
  action:      string;
  target_type: string | null;
  target_id:   string | null;
  result:      string;
  note:        string | null;
  jti:         string | null;
  trace_id:    string | null;
  session_id:  string | null;
  client_ip:   string | null;
  details:     Record<string, unknown>;
}


export interface AuditLogPage {
  rows:     AuditLogRow[];
  limit:    number;
  offset:   number;
  has_more: boolean;
}


export interface AuditLogFilters {
  actor_id?:    string;
  action?:      string;
  target_type?: string;
  since?:       string;     // ISO 8601
  until?:       string;
  limit?:       number;
  offset?:      number;
}


export const observabilityApi = {
  llmCost(windowDays: number = 30, opts?: RequestOptions): Promise<CostReport> {
    return api.get<CostReport>(
      `/observability/llm-cost?window_days=${windowDays}`, opts,
    );
  },

  auditLog(filters: AuditLogFilters = {}, opts?: RequestOptions): Promise<AuditLogPage> {
    const q = new URLSearchParams();
    if (filters.actor_id)    q.set("actor_id",    filters.actor_id);
    if (filters.action)      q.set("action",      filters.action);
    if (filters.target_type) q.set("target_type", filters.target_type);
    if (filters.since)       q.set("since",       filters.since);
    if (filters.until)       q.set("until",       filters.until);
    q.set("limit",  String(filters.limit ?? 50));
    q.set("offset", String(filters.offset ?? 0));
    return api.get<AuditLogPage>(`/observability/audit-log?${q.toString()}`, opts);
  },
};
